/**
 * Renders Trip tooltip content
 */
import React, { FC, useState, useRef, useEffect } from 'react';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { ReactComponent as PickupIcon } from '../../assets/icons/pickup_icon.svg';
import { ReactComponent as DeliveryIcon } from '../../assets/icons/delivery_icon.svg';
import { ReactComponent as StopIcon } from '../../assets/icons/stop_icon.svg';
import TwLoadTags from '../TwLoadTag/TwLoadTag';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { TwTripTooltipInfoProps } from './TwLoadTooltipInfo';
import {
  DateTime,
  DateTimeWrapper,
  LocationIconsWrapper,
  PickupDeliveryWrapper
} from './TwLoadTooltipContent';
import LoadUtils from '../../utils/loadUtils';
import { TRIP_STATUS, TripStatusColor } from '../../redux/api/trips/trips.model';
import { EntityType } from '../../redux/Global.model';

const TwTripTooltipContent: FC<{ tripData?: TwTripTooltipInfoProps }> = ({ tripData }) => {
  const { enableFlexibleTagging } = useFeatureFlags();
  const [locationsLineHeight, setLocationsLineHeight] = useState(0);
  const firstStopRef = useRef<HTMLDivElement>(null);
  const numberStopsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (firstStopRef.current) {
      let height = 0;
      height = firstStopRef.current.clientHeight;

      if (numberStopsRef.current) {
        height += numberStopsRef.current.clientHeight + 8; // 8px for margin
      }

      setLocationsLineHeight(height);
    }
  }, [firstStopRef, numberStopsRef]);

  const getTripColor = (status: TRIP_STATUS): string => {
    const color = TripStatusColor[status];

    if (!color || color === undefined) {
      return '#6B7280'; // Gray
    } else {
      return color;
    }
  };

  return (
    <Box>
      {tripData && (
        <>
          <Stack
            className={`tw-load-tooltip__status ${
              tripData.unassigned ? 'tw-load-tooltip__status-unassigned' : ''
            }`}
            sx={{
              backgroundColor: `${getTripColor(tripData.tripStatus)}20`
            }}
          >
            <Typography
              className="tw-load-tooltip__status-title"
              sx={{
                color: tripData.unassigned ? '#111827' : getTripColor(tripData.tripStatus)
              }}
            >
              {tripData.unassigned
                ? 'Unassigned'
                : LoadUtils.getTripStatusReadableText(tripData.tripStatus)}
            </Typography>
          </Stack>

          <Box className="tw-load-tooltip__data-container">
            {enableFlexibleTagging && tripData.tags.length > 0 && (
              <TwLoadTags
                loadId={tripData.loadId}
                tags={tripData.tags}
                readOnly
                entity={EntityType.TRIP}
              />
            )}
            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              sx={{
                marginTop: enableFlexibleTagging && tripData.tags.length > 0 ? '16px' : '0'
              }}
            >
              <Stack className="tw-load-tooltip__client-name">
                <Typography>{tripData.clientName}</Typography>
              </Stack>
            </Stack>

            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              sx={{ marginTop: '16px' }}
            >
              <LocationIconsWrapper>
                <PickupIcon style={{ minWidth: '12px' }} />
                <Box
                  className="tw-load-tooltip__locations-line"
                  sx={{ height: locationsLineHeight }}
                ></Box>
                <DeliveryIcon style={{ minWidth: '12px' }} />
              </LocationIconsWrapper>

              <PickupDeliveryWrapper>
                <Stack ref={firstStopRef} className="tw-load-tooltip__locations-info">
                  <p>{tripData.pickupLocation?.name || ''}</p>
                  <p>
                    {tripData.pickupLocation &&
                      `${tripData.pickupLocation?.address_1}, ${tripData.pickupLocation?.city}, ${tripData.pickupLocation?.state}`}
                  </p>
                  <p>{tripData.pickupNumber && `Pickup #${tripData.pickupNumber}`}</p>
                </Stack>

                {!!tripData.stops && (
                  <Box ref={numberStopsRef} className="tw-load-tooltip__locations-stops">
                    <StopIcon style={{ minWidth: '12px' }} />
                    {`+${tripData.stops} stop${Number(tripData.stops) > 1 ? 's' : ''} `}
                  </Box>
                )}

                <Stack className="tw-load-tooltip__locations-info">
                  <p>{tripData.deliveryLocation?.name || ''}</p>
                  <p>
                    {tripData.deliveryLocation &&
                      `${tripData.deliveryLocation?.address_1}, ${tripData.deliveryLocation?.city}, ${tripData.deliveryLocation?.state}`}
                  </p>
                  <p>{tripData.deliveryNumber && `Delivery #${tripData.deliveryNumber}`}</p>
                </Stack>
              </PickupDeliveryWrapper>

              <DateTimeWrapper>
                <DateTime>
                  <span>{tripData.pickupDate}</span>
                  <span>{tripData.pickupStartTime}</span>
                  <span>{tripData.pickupEndTime}</span>
                </DateTime>
                <DateTime>
                  <span>{tripData.deliveryDate}</span>
                  <span>{tripData.deliveryStartTime}</span>
                  <span>{tripData.deliveryEndTime}</span>
                </DateTime>
              </DateTimeWrapper>
            </Stack>

            <Stack className="tw-load-tooltip__boxes-container">
              <Box className="tw-load-tooltip__boxes">
                <Typography>Order #</Typography>
                <Typography component={'span'}>{tripData.loadReadableId}</Typography>
              </Box>
              <Box className="tw-load-tooltip__boxes">
                <Typography>Driver</Typography>
                <Typography component={'span'}>{`${
                  tripData.driverName ? tripData.driverName : '-'
                }`}</Typography>
              </Box>

              <Box className="tw-load-tooltip__boxes">
                <Typography>Truck</Typography>
                <Typography component={'span'}>{`${
                  tripData.truckName ? tripData.truckName : '-'
                }`}</Typography>
              </Box>
              <Box className="tw-load-tooltip__boxes">
                <Typography>Trailer</Typography>
                <Typography component={'span'}>{`${
                  tripData.trailerName ? tripData.trailerName : '-'
                }`}</Typography>
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TwTripTooltipContent;
